import { useTranslation } from "react-i18next";
import { displayToast } from "../../../components/app/AppToast";
import { apiClient, useApiRevalidate } from "../../../services/api";
import { conversationIdEngine } from "../../assistant/conversationEngine";
import { isSharing, shareUrl } from "../../app/utils/shareUrl";
import {
  gaEventGptConversationShared,
  gaEventGptConversationShareRevoked,
} from "../../../tools/analytics/domains/gpt.analytics";

/** Who sees Share and Stop sharing on a JuisciGPT conversation. Webapp only. */
export const CONVERSATION_SHARE_FLAG = "gpt-conversation-share";

// Share the conversation as it is now and hand the /gpt/shared/:token link to
// the native/web share sheet. A conversation keeps one link: sharing it again
// gives the same link, which shows the new turns once it has left (copied or
// sent). A sheet that opens and closes publishes nothing new.
//
// The link is minted inside the click, before the sheet opens, because the
// sheet needs it there. A sheet that closes unsent, or a copy that fails,
// shared nothing: a link this click minted is undone, and a link already given
// out stays. The server says which, since the page's `isShared` can lag behind
// (a share sent a moment ago, another device). A click while another share
// runs opens nothing, so it mints nothing either.
//
// Shared by BOTH chat surfaces: a Chat-with-Paper transcript is a
// `GptConversation` like any other, so it shares through the same token, the
// same public reader, and the same analytics event.
export function useShareConversation(): (
  conversationId: string,
  questionCount: number
) => Promise<void> {
  const { t } = useTranslation();
  const revalidate = useApiRevalidate();
  return async (conversationId, questionCount) => {
    if (isSharing()) return;
    try {
      // Not awaited: `shareUrl` must start inside the click (see there).
      const minted =
        conversationIdEngine(conversationId) === "assistant"
          ? apiClient
              .post("/assistant/conversations/:conversationId/share", {
                param: { conversationId },
                query: { snapshot: "keep" },
              })
              .then(({ body }) => body)
          : apiClient
              .post("/webapp/private/conversations/:id/share", {
                param: { id: conversationId },
                query: { snapshot: "keep" },
              })
              .then(({ body }) => body);
      const link = minted.then(
        ({ shareToken }) =>
          `${globalThis.location.origin}/gpt/shared/${shareToken}`
      );
      // The link alone, as ChatGPT and Claude share a conversation: a copy from
      // the sheet gives the link, not a sentence around it.
      const outcome = await shareUrl(
        t("JuisciGPT conversation"),
        null,
        link
      ).catch(async (error: unknown) => {
        await undoMinted(conversationId, minted);
        await revalidateSharedState(revalidate, conversationId);
        throw error;
      });
      switch (outcome) {
        // The desktop copy says "Link copied" itself (`shareUrl`).
        case "copied":
          await publishNewTurns(conversationId, minted);
          gaEventGptConversationShared({
            conversationId,
            // The id says which engine owns the conversation.
            engine: conversationIdEngine(conversationId),
            questionCount,
            channel: "copy_link",
            shareLink: (await minted).share,
          });
          break;
        // The sheet does not say which target took the link: copy, Messages or
        // another app. It only says the link left. The Android app's chooser
        // says nothing at all, so there "sent" means the chooser opened: a
        // dismissed chooser still publishes the new turns (known limitation,
        // docs/juisci-gpt-conversation-persistence.md).
        case "sent":
          await publishNewTurns(conversationId, minted);
          displayToast(t("Link shared"), "success");
          gaEventGptConversationShared({
            conversationId,
            // The id says which engine owns the conversation.
            engine: conversationIdEngine(conversationId),
            questionCount,
            channel: "share_sheet",
            shareLink: (await minted).share,
          });
          break;
        case "cancelled":
          await undoMinted(conversationId, minted);
          break;
        // `isSharing` above turned this click away before it minted.
        case "busy":
          throw new Error("should not happen: a share ran beside another");
        default:
          throw new Error(outcome satisfies never);
      }
      // The menus offer Stop sharing from `isShared`.
      await revalidateSharedState(revalidate, conversationId);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };
}

type Minted = Promise<{ share: "created" | "existing" }>;

// A link this click minted is undone; a link already given out stays.
async function undoMinted(
  conversationId: string,
  minted: Minted
): Promise<void> {
  if ((await minted).share === "created") await deleteShare(conversationId);
}

// A link minted by this click already shows every turn; a link given out
// before shows the turns asked since only now that it left again.
async function publishNewTurns(
  conversationId: string,
  minted: Minted
): Promise<void> {
  if ((await minted).share === "created") return;
  await (conversationIdEngine(conversationId) === "assistant"
    ? apiClient.put("/assistant/conversations/:conversationId/share", {
        param: { conversationId },
      })
    : apiClient.put("/webapp/private/conversations/:id/share", {
        param: { id: conversationId },
      }));
}

// Awaited by its callers, inside their try.
function deleteShare(conversationId: string) {
  return conversationIdEngine(conversationId) === "assistant"
    ? apiClient.delete("/assistant/conversations/:conversationId/share", {
        param: { conversationId },
      })
    : apiClient.delete("/webapp/private/conversations/:id/share", {
        param: { id: conversationId },
      });
}

// The list rows, the open conversation and a paper's chat history carry `isShared`. The assistant's
// history lists the legacy rows too.
async function revalidateSharedState(
  revalidate: ReturnType<typeof useApiRevalidate>,
  conversationId: string
): Promise<void> {
  await revalidate("/assistant/conversations");
  if (conversationIdEngine(conversationId) === "assistant") {
    await revalidate("/assistant/conversations/:conversationId", {
      param: { conversationId },
    });
    return;
  }
  await revalidate("/webapp/private/conversations");
  await revalidate("/webapp/private/conversations/:id", {
    param: { id: conversationId },
  });
  // A Chat-with-Paper conversation reads its state from its paper's history,
  // whose key is the paper's id, which the caller does not hold: all of them.
  await revalidate("/webapp/private/articles/:id/chat-with-paper/history", {});
}

// Stop sharing: the link stops working, and a later share mints a new one.
export function useUnshareConversation(): (
  conversationId: string
) => Promise<void> {
  const { t } = useTranslation();
  const revalidate = useApiRevalidate();
  return async (conversationId) => {
    try {
      await deleteShare(conversationId);
      gaEventGptConversationShareRevoked({
        conversationId,
        engine: conversationIdEngine(conversationId),
      });
      await revalidateSharedState(revalidate, conversationId);
      displayToast(t("This conversation is no longer shared"), "success");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };
}
